/**
 * ----------------------------------------------------------------------------
 * DeviceAccessForm Component
 * ----------------------------------------------------------------------------
 * Summary:
 * Form component for managing device access per user.
 * Select a user to view and modify their assigned devices —
 * add or remove multiple devices at once and save changes.
 *
 * @author Chandan Singh
 * @created 2026-05-15
 * ----------------------------------------------------------------------------
 */
import React, { useState, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import { FormattedMessage } from 'react-intl';
import "./DeviceAccessForm.css";
import UnsavedChangesPopupHOC from "../../../shared/components/Popup/hoc/UnsavedChangesPopupHOC";
import UnsavedChangesPopup from "../../../shared/components/Popup/UnsavedChanges";
import IState from "../../../core/models/IState";
import { useDispatch, useSelector } from "react-redux";
import * as actions from '../../../core/actions/actions';
import { formatMessage } from '../../../translations/connectedIntlProvider'
import SensorIcon from '../../../assets/Images/icon/svg/sensor-icon.svg'
import UserIcon from '../../../assets/Images/icon/svg/user-icon.svg'

function DeviceAccessForm(props: any) {
    const { allDevices, isLoading, onModalClosed, submitHandler, allUsersData } = props;
    const USERS = allUsersData || [];
    const DEVICES = allDevices || [];

    const [selectedUser, setSelectedUser] = useState<any>(null);
    const [userId, setUserID] = useState<number>(0);
    const [checkedDevices, setCheckedDevices] = useState<string[]>([]);
    const [hasClicked, sethasClicked] = useState<boolean>(false);
    const [pendingUser, setPendingUser] = useState<any>(null);

    const [userSearch, setUserSearch] = useState<string>("");
    const [deviceSearch, setDeviceSearch] = useState<string>("");
    const [accessSearch, setAccessSearch] = useState<string>("");
    const dispatch = useDispatch();

    const stateAllDevice = (state: IState) => state.user.userDevices;
    const userDevices = useSelector(stateAllDevice);

    useEffect(() => {
        if (userId) dispatch(actions.userDevices(userId))
    }, [userId])

    useEffect(() => {
        const showAll = localStorage.getItem('userType') === 'General';
        if (showAll) {
            setSelectedUser(USERS[0])
            setUserID(USERS[0]?.Id)
        }
    }, [USERS])

    useEffect(() => {
        if (userDevices?.devices) {
            const nonPrimary = userDevices.devices
                .filter((d: any) => !d.IsPrimary)
                .map((d: any) => ({
                    ID: d.DeviceId,
                    ICCID: d.Imsi,
                    UserId: d.UserId
                }));
            setCheckedDevices(nonPrimary);
        }
    }, [userDevices]);

    useEffect(() => {
        return () => {
            dispatch(actions.resetUserDevicesState());
        };
    }, []);

    const devicesId = (userDevices?.devices?.filter((d: any) => !d.IsPrimary) || [])
        .map((d: any) => ({
            ID: d.DeviceId,
            ICCID: d.Imsi,
            UserId: d.UserId,
        }));
    const primaryDevice = userDevices?.devices?.filter((d: any) => d.IsPrimary) || []

    const filteredUsers = USERS.filter((user: any) => {
        const query = userSearch.toLowerCase();
        return (
            user.UserName?.toLowerCase().includes(query) ||
            user.Email?.toLowerCase().includes(query)
        );
    });

    const filteredDevices = DEVICES.filter((device: any) =>
        device.ICCID?.toLowerCase().includes(deviceSearch.toLowerCase())
    );

    const filteredAccessDevices = checkedDevices.filter((device: any) =>
        device.ICCID?.toLowerCase().includes(accessSearch.toLowerCase())
    );

    const filteredPrimaryDevices = primaryDevice.filter((pd: any) =>
        pd.Imsi?.toLowerCase().includes(accessSearch.toLowerCase())
    )

    const handleUserSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setUserSearch(value);
    }

    const handleDeviceSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setDeviceSearch(value);
    }

    const handleAccessSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setAccessSearch(value);
    }

    const arraysEqual = (arr1: any, arr2: any) => {
        if (arr1.length !== arr2.length) return false;
        return arr1.every((value: any, index: number) => value.ID === arr2[index].ID);
    }

    const handleUserClick = (user: any) => {
        setAccessSearch('');
        setDeviceSearch('');
        if (hasClicked) {
            if (!arraysEqual(devicesId, checkedDevices)) {
                props.unsavedChangesToggleHandler();
                setPendingUser(user);
                return;
            }
        }
        setSelectedUser(user);
        setUserID(user.Id)
        sethasClicked(true);
    }

    const handleDeviceToggle = (device: any) => {
        setCheckedDevices((prev) => {
            const exists = prev.some((d: any) => d.ID === device.ID)
            if (exists) return prev.filter((d: any) => d.ID !== device.ID)
            return [...prev, device];
        });
    }

    const handleRemoveAccess = (device: any) => {
        setCheckedDevices((prev) => {
            return prev.filter((d: any) => d.ICCID !== device.ICCID)
        });
    }

    const resetState = () => {
        setCheckedDevices(devicesId);
    };

    const handleSubmit = () => {
        if (!selectedUser) {
            onModalClosed();
            return
        }
        if (arraysEqual(devicesId, checkedDevices)) {
            onModalClosed()
            return
        }
        if (props.showUnsavedChangesPopup) {
            submitHandler(selectedUser, checkedDevices, primaryDevice, false);
            // resetState();
            props.unsavedChangesToggleHandler();
            setSelectedUser(pendingUser);
            setUserID(pendingUser.Id)
        } else {
            submitHandler(selectedUser, checkedDevices, primaryDevice, true);
            // resetState();
        }
    }

    const onModalCloses = () => {
        props.unsavedChangesToggleHandler();
        resetState();
        setSelectedUser(pendingUser);
        setUserID(pendingUser.Id)
    };

    return (
        <>
            {props.showUnsavedChangesPopup && (
                <UnsavedChangesPopup modalSubmit={handleSubmit} modalClosed={onModalCloses} userInfo={selectedUser} isLoading={false} />
            )}
            <Modal
                className="add-user-modal device-pop-up device-access-modal"
                show={isLoading}
                centered
                onHide={onModalClosed}
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FormattedMessage id='Manage_User_Device_Access' />
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <div className="udf-grid">
                        <div className="udf-column">
                            <div className="udf-col-header">
                                <FormattedMessage id='Users' />
                                <span className="udf-col-header-count">{USERS.length}</span>
                            </div>
                            <div className="udf-search-wrapper">
                                <input
                                    type="text"
                                    className="udf-search-input"
                                    placeholder={formatMessage('Search_Users')}
                                    value={userSearch}
                                    onChange={handleUserSearch}
                                />
                            </div>
                            <div className="udf-col-body">
                                {filteredUsers.length === 0 ? (
                                    <div className="udf-no-results"><FormattedMessage id='No_Users_Found' /></div>
                                ) : (
                                    filteredUsers.map((user: any) => (
                                        <div
                                            key={user.Id}
                                            className={`udf-row udf-user-row ${selectedUser?.Email === user.Email ? "udf-row--active" : ""}`}
                                            onClick={() => handleUserClick(user)}
                                            title={user.Email}
                                        >
                                            <span className="udf-email">{user.UserName}</span>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>

                        <div className="udf-column">
                            <div className="udf-col-header">
                                <FormattedMessage id='Device_List' />
                                <span className="udf-col-header-count">{DEVICES.length}</span>
                            </div>
                            {selectedUser ? (
                                <div className="udf-search-wrapper">
                                    <input
                                        type="text"
                                        className="udf-search-input"
                                        placeholder={formatMessage('Search_Devices')}
                                        value={deviceSearch}
                                        onChange={handleDeviceSearch}
                                        disabled={DEVICES.length===0}
                                    />
                                </div>
                            ) : (
                                <div className="udf-search-placeholder" />
                            )}
                            <div className="udf-col-body">
                                {!selectedUser ? (
                                    <div className="udf-placeholder d-flex flex-column align-items-center">
                                        <img src={UserIcon} width={120} height={120} className="mt-3" alt="sensor" />
                                        <FormattedMessage id='Select_A_User_To_View_Devices' />
                                    </div>
                                ) : filteredDevices.length === 0 ? (
                                    <div className="udf-no-results d-flex flex-column align-items-center">
                                        <img src={SensorIcon} width={120} height={120} className="mt-3" alt="sensor" />
                                        <FormattedMessage id='No_Device_Found' />
                                    </div>
                                ) : (
                                    <>
                                        {filteredDevices.slice().sort((a: any, b: any) => {
                                            const aIsPrimary = primaryDevice.some((pd: any) => pd.Imsi === a.ICCID);
                                            const bIsPrimary = primaryDevice.some((pd: any) => pd.Imsi === b.ICCID);
                                            return Number(bIsPrimary) - Number(aIsPrimary);
                                        }).map((device: any, index: number, arr: any[]) => {
                                            const isPrimary = primaryDevice.some((pd: any) => pd.Imsi === device.ICCID);
                                            const prevIsPrimary = index > 0 && primaryDevice.some((pd: any) => pd.Imsi === arr[index - 1].ICCID);
                                            const showSecondaryHeader = !isPrimary && prevIsPrimary;
                                            return (
                                                <React.Fragment key={device.ID}>
                                                    <label
                                                        className="udf-row udf-device-row"
                                                        htmlFor={`chk-${device.ID}`}
                                                        title={device.ICCID}
                                                    >
                                                        <span className="udf-device-name">
                                                            {device.ICCID}
                                                            {isPrimary && (
                                                                <span className="udf-primary-badge"><FormattedMessage id='Primary' /></span>
                                                            )}
                                                        </span>
                                                        <input
                                                            type="checkbox"
                                                            className="udf-checkbox"
                                                            checked={checkedDevices.some((d: any) => d.ID === device.ID) || primaryDevice?.some((pd: any) => pd.Imsi === device.ICCID)}
                                                            onChange={() => handleDeviceToggle(device)}
                                                            id={`chk-${device.ID}`}
                                                            disabled={isPrimary}
                                                        />
                                                    </label>
                                                </React.Fragment>
                                            );
                                        })}
                                    </>
                                )}
                            </div>
                        </div>

                        <div className="udf-column">
                            <div className="udf-col-header">
                                <FormattedMessage id='Access_Granted' />
                                <span className="udf-col-header-count">{primaryDevice.length + checkedDevices.length}</span>
                            </div>
                            {selectedUser ? (
                                <div className="udf-search-wrapper">
                                    <input
                                        type="text"
                                        className="udf-search-input"
                                        placeholder={formatMessage("Search_Access")}
                                        value={accessSearch}
                                        onChange={handleAccessSearch}
                                        disabled={primaryDevice.length===0 && checkedDevices.length===0}
                                    />
                                </div>
                            ) : (
                                <div className="udf-search-placeholder" />
                            )}
                            <div className="udf-col-body">
                                {!selectedUser ? (
                                    <div className="udf-placeholder d-flex flex-column align-items-center">
                                        <img src={UserIcon} width={120} height={120} className="mt-3" alt="sensor" />
                                        <FormattedMessage id='Select_A_User_To_View_Devices' />
                                    </div>
                                ) : checkedDevices.length === 0 && primaryDevice.length === 0 ? (
                                    <div className="udf-placeholder d-flex flex-column align-items-center">
                                        <img src={SensorIcon} width={120} height={120} className="mt-3" alt="sensor" />
                                        <FormattedMessage id='No_Devices_Selected' />
                                    </div>
                                ) : filteredAccessDevices.length === 0 && filteredPrimaryDevices.length === 0 ? (
                                    <div className="udf-no-results"><FormattedMessage id='No_Access_Devices_Found' /></div>
                                ) : (
                                    <>
                                        {filteredPrimaryDevices?.length > 0 && (
                                            <div className="udf-section-header">
                                                <FormattedMessage id='Primary' />
                                                <span className="udf-section-header-count">{filteredPrimaryDevices.length}</span>
                                            </div>
                                        )}
                                        {selectedUser && filteredPrimaryDevices?.map((pd: any) => (
                                            <div key={pd.Imsi} className="udf-row udf-access-row udf-access-row--primary">
                                                <span className="udf-tick">&#10003;</span>
                                                <span className="udf-device-name">
                                                    {pd.Imsi}
                                                    <span className="udf-primary-badge"><FormattedMessage id='Primary' /></span>
                                                </span>
                                            </div>
                                        ))}
                                        {filteredAccessDevices.length > 0 && (
                                            <div className="udf-section-header">
                                                <FormattedMessage id='Secondary' />
                                                <span className="udf-section-header-count">{filteredAccessDevices.length}</span>
                                            </div>
                                        )}
                                        {filteredAccessDevices.map((device: any) => (
                                            <div key={device.ICCID} className="udf-row udf-access-row">
                                                <span className="udf-tick">&#10003;</span>
                                                <span className="udf-device-name">{device.ICCID}</span>
                                                <button
                                                    className="udf-remove-btn"
                                                    onClick={() => handleRemoveAccess(device)}
                                                    title={formatMessage('Remove')}
                                                >
                                                    &#x2715;
                                                </button>
                                            </div>
                                        ))}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </Modal.Body>

                <div className="modal-footer">
                    <div className="form-group row tab-action">
                        <div className="col-12 px-0">
                            <Button variant="primary" onClick={onModalClosed} className="btn btn-primary">
                                <FormattedMessage id='Button_Cancel' />
                            </Button>
                            <Button variant="primary" onClick={handleSubmit} disabled={selectedUser === null || primaryDevice.length == 0 && checkedDevices.length == 0}
                                className={selectedUser === null || primaryDevice.length == 0 && checkedDevices.length == 0 ? 'button-disable' : 'btn btn-blue btn btn-primary'}
                            >
                                <FormattedMessage id='Button_Save' />
                            </Button>
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    );
}

export default UnsavedChangesPopupHOC(DeviceAccessForm);